// Player settings (O): sound levels and display tastes, kept in this browser's localStorage.
// Anything that reads a setting does so live (Settings.get), so changes apply at once.

const KEY = 'reclaim-settings';

export type SettingsData = {
  master: number; // 0..1
  effects: number;
  ambience: number;
  shake: number; // 0..1 multiplier on screen shake
  hurtFlash: boolean;
  damageNumbers: boolean;
  night: number; // 0.5..1, how dark nights get
  showHint: boolean;
  showMinimap: boolean;
  zoom: number;
};

const DEFAULTS: SettingsData = { master: 0.8, effects: 1, ambience: 0.8, shake: 1, hurtFlash: true, damageNumbers: true, night: 1, showHint: true, showMinimap: true, zoom: 1.25 };

type Row =
  | { key: keyof SettingsData; label: string; kind: 'range'; min: number; max: number; step: number; fmt: (v: number) => string }
  | { key: keyof SettingsData; label: string; kind: 'check' };

const pct = (v: number) => `${Math.round(v * 100)}%`;
const SECTIONS: { title: string; rows: Row[] }[] = [
  { title: 'Sound', rows: [
    { key: 'master', label: 'Master volume', kind: 'range', min: 0, max: 1, step: 0.05, fmt: pct },
    { key: 'effects', label: 'Effects', kind: 'range', min: 0, max: 1, step: 0.05, fmt: pct },
    { key: 'ambience', label: 'Ambience', kind: 'range', min: 0, max: 1, step: 0.05, fmt: pct },
  ] },
  { title: 'Display', rows: [
    { key: 'shake', label: 'Screen shake', kind: 'range', min: 0, max: 1, step: 0.1, fmt: (v) => (v ? pct(v) : 'off') },
    { key: 'night', label: 'Night darkness', kind: 'range', min: 0.5, max: 1, step: 0.05, fmt: pct },
    { key: 'hurtFlash', label: 'Red flash when hurt', kind: 'check' },
    { key: 'damageNumbers', label: 'Damage numbers', kind: 'check' },
    { key: 'showHint', label: 'Controls hint (top right)', kind: 'check' },
    { key: 'showMinimap', label: 'Minimap', kind: 'check' },
  ] },
];

class SettingsStore {
  private data: SettingsData;
  private listeners: ((s: SettingsData) => void)[] = [];
  private panel: HTMLElement | null = null;

  constructor() {
    let saved: Partial<SettingsData> = {};
    try {
      saved = JSON.parse(localStorage.getItem(KEY) ?? '{}') ?? {};
    } catch {}
    this.data = { ...DEFAULTS };
    for (const k of Object.keys(DEFAULTS) as (keyof SettingsData)[]) if (typeof saved[k] === typeof DEFAULTS[k]) (this.data as Record<string, unknown>)[k] = saved[k];
  }

  get(): SettingsData {
    return this.data;
  }

  set<K extends keyof SettingsData>(k: K, v: SettingsData[K]): void {
    this.data[k] = v;
    localStorage.setItem(KEY, JSON.stringify(this.data));
    for (const f of this.listeners) f(this.data);
  }

  /** Calls f now and on every change. */
  watch(f: (s: SettingsData) => void): void {
    this.listeners.push(f);
    f(this.data);
  }

  get open(): boolean {
    return Boolean(this.panel && !this.panel.classList.contains('hidden'));
  }

  toggle(show = !this.open): void {
    if (!this.panel) this.build();
    this.panel!.classList.toggle('hidden', !show);
  }

  private build(): void {
    const p = document.createElement('div');
    p.id = 'settings';
    p.className = 'hidden';
    p.addEventListener('mousedown', (e) => e.stopPropagation());
    p.addEventListener('wheel', (e) => e.stopPropagation());
    const h = document.createElement('h2');
    h.textContent = 'Settings';
    p.append(h);
    for (const sec of SECTIONS) {
      const t = document.createElement('h3');
      t.textContent = sec.title;
      p.append(t);
      for (const row of sec.rows) {
        const line = document.createElement('label');
        line.className = 'set-row';
        const name = document.createElement('span');
        name.textContent = row.label;
        const input = document.createElement('input');
        line.append(name, input);
        if (row.kind === 'range') {
          input.type = 'range';
          Object.assign(input, { min: String(row.min), max: String(row.max), step: String(row.step), value: String(this.data[row.key]) });
          const out = document.createElement('output');
          out.textContent = row.fmt(this.data[row.key] as number);
          input.addEventListener('input', () => {
            this.set(row.key, Number(input.value) as never);
            out.textContent = row.fmt(Number(input.value));
          });
          // Let go of the slider afterwards, so arrow keys walk again instead of nudging it.
          input.addEventListener('change', () => input.blur());
          line.append(out);
        } else {
          input.type = 'checkbox';
          input.checked = Boolean(this.data[row.key]);
          input.addEventListener('change', () => {
            this.set(row.key, input.checked as never);
            input.blur();
          });
        }
        p.append(line);
      }
    }
    const foot = document.createElement('div');
    foot.className = 'set-foot';
    const reset = document.createElement('button');
    reset.textContent = 'Defaults';
    reset.onclick = () => {
      for (const k of Object.keys(DEFAULTS) as (keyof SettingsData)[]) this.set(k, DEFAULTS[k] as never);
      p.remove();
      this.panel = null;
      this.toggle(true);
    };
    const close = document.createElement('button');
    close.textContent = 'Close (O / Esc)';
    close.onclick = () => this.toggle(false);
    const tip = document.createElement('p');
    tip.textContent = 'Saved in this browser. M mutes everything; + / − zoom is remembered too.';
    foot.append(reset, close);
    p.append(foot, tip);
    document.getElementById('hud')!.append(p);
    this.panel = p;
  }
}

export const Settings = new SettingsStore();

/** O toggles the panel, Esc closes it (typing in a box doesn't count). */
document.addEventListener('keydown', (e) => {
  if ((e.target as HTMLElement).tagName === 'INPUT' && (e.target as HTMLInputElement).type === 'text') return;
  if (e.key === 'o' || e.key === 'O') Settings.toggle();
  else if (e.key === 'Escape' && Settings.open) Settings.toggle(false);
});
